/**
 * `useBoardSource` is the issue-domain seam between the board renderer and the
 * server. It owns the endpoint the board fetches from, the response shape it
 * expects back, and the mapping from that payload into the generic
 * {@link BoardSource} the renderer consumes.
 *
 * Today it fetches the Rails Issues list route (`GET /:owner/:repo/issues?q=…&page=1`)
 * and groups the flat list-view response client-side by label. When the board's
 * dedicated REST endpoint lands, swap the fetch and the mapper here — the
 * renderer and the generic board pieces do not need to change.
 *
 * Paging isn't wired yet (github/views#139); the first page of results is
 * grouped client-side and `hasMore*` axes are always `false`.
 */

import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {
  type RepoIssuesDashboardContentResponse,
  RepoIssuesDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {replaceDateTokens} from '@github-ui/issue-url-helper'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {queryOptions, useSuspenseQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import type {BoardUrlParams} from '../../utils/board-url-params'
import {type BoardSource, toBoardData} from './generic/to-board-data'
import {resultsToBoardPayload} from './issues/proto-to-board-payload-mapper'

export interface BoardSourceInput {
  owner: string
  repo: string
  query: string
  params: BoardUrlParams
}

// The Rails route wraps the payload under its route id; extract it before decoding.
interface DataRouterResponse {
  payload?: Record<string, unknown>
}

const ROUTE_ID = 'issuesIndexContentRoute'

function boardIssuesQueryOptions({owner, repo, query}: {owner: string; repo: string; query: string}) {
  const resolvedQuery = replaceDateTokens(query)
  return queryOptions({
    queryKey: ['board-issues', owner, repo, resolvedQuery] as const,
    queryFn: async ({signal}) => {
      const {json, isProtoJson} = await queryFnFetchWithProtoSignal<DataRouterResponse>({
        routeId: 'issuesIndexContentRoute',
        queryName: 'boardIssues',
        queryDeps: {
          pathname: `/${owner}/${repo}/issues`,
          searchParams: {q: resolvedQuery, page: '1'},
          init: {signal},
        },
      })
      const payload = json.payload?.[ROUTE_ID]
      if (!payload) throw new Error('Unable to find the repository issues payload')
      return isProtoJson
        ? fromJson({
            schema: RepoIssuesDashboardContentResponseSchema,
            raw: payload,
            routeId: ROUTE_ID,
            queryName: 'boardIssues',
          })
        : (payload as RepoIssuesDashboardContentResponse)
    },
  })
}

/**
 * Fetches the issues/PRs the board renders and maps them into a {@link BoardSource}.
 * Suspends until the initial page returns; callers must render inside a Suspense boundary.
 */
export function useBoardSource({owner, repo, query}: BoardSourceInput): BoardSource {
  const {data} = useSuspenseQuery(boardIssuesQueryOptions({owner, repo, query}))
  return useMemo(() => toBoardData(resultsToBoardPayload(data.results)), [data.results])
}
